import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AppLayout from './components/layout/AppLayout';
import LoginPage from './pages/LoginPage';

// Lazy on purpose. A static import would keep Radix and lucide in the main
// production chunk even though the route below is dead-coded away — Rollup
// cannot prove the module is side-effect free. This puts the gallery in its
// own chunk that production never requests.
const UiGalleryPage = React.lazy(() => import('./pages/UiGalleryPage'));
import EventListPage from './pages/EventListPage';
import EventDetailPage from './pages/EventDetailPage';
import RegistrationDetailPage from './pages/RegistrationDetailPage';
import ExportPage from './pages/ExportPage';
import RaceDataPage from './pages/RaceDataPage';
import TracksPage from './pages/admin/TracksPage';
import SanctioningBodiesPage from './pages/admin/SanctioningBodiesPage';
import UsersPage from './pages/admin/UsersPage';
import UserDetailPage from './pages/admin/UserDetailPage';
import AppUsersPage from './pages/admin/AppUsersPage';
import AppUserDetailPage from './pages/admin/AppUserDetailPage';
import { features } from './config/features';
import type { FeatureName } from './config/features';
import { Button } from '@racing/ui';

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Unhandled error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-canvas p-8 text-center">
          <h1 className="font-sans text-xl font-bold text-ink">Something went wrong</h1>
          <p className="max-w-prose font-sans text-base text-ink-soft">
            An unexpected error occurred. Reloading usually clears it; if it keeps
            happening, the details are in the browser console.
          </p>
          <Button className="mt-2" onClick={() => window.location.reload()}>
            Reload page
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAdmin, isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/events" replace />;
  return <>{children}</>;
};

const TrackOperatorRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAdmin, isTrackOperator, isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin && !isTrackOperator) return <Navigate to="/events" replace />;
  return <>{children}</>;
};

// Routes belonging to a feature that is switched off (see config/features.ts).
// The tabs that link to them are hidden, so this only catches a typed URL or a
// stale bookmark. UI gating only — the server endpoints are unaffected.
const FeatureRoute: React.FC<{ feature: FeatureName; children: React.ReactNode }> = ({
  feature,
  children,
}) => {
  if (!features[feature]) return <Navigate to="/events" replace />;
  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Component gallery for the Tailwind design system. Dev-only: it is a
          build-time constant, so the route and the page are tree-shaken out of
          production bundles entirely. */}
      {import.meta.env.DEV && (
        <Route
          path="/ui"
          element={
            <React.Suspense fallback={null}>
              <UiGalleryPage />
            </React.Suspense>
          }
        />
      )}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/events" replace />} />
        <Route path="events" element={<EventListPage />} />
        <Route path="events/new" element={<EventDetailPage />} />
        <Route path="events/:id" element={<EventDetailPage />} />
        <Route
          path="events/:id/registrations/:regId"
          element={<FeatureRoute feature="eventRegistrations"><RegistrationDetailPage /></FeatureRoute>}
        />
        <Route
          path="events/:id/export"
          element={<FeatureRoute feature="eventRegistrations"><ExportPage /></FeatureRoute>}
        />
        <Route path="race-data" element={<TrackOperatorRoute><RaceDataPage /></TrackOperatorRoute>} />
        <Route path="admin/tracks" element={<AdminRoute><TracksPage /></AdminRoute>} />
        <Route path="admin/sanctioning-bodies" element={<AdminRoute><SanctioningBodiesPage /></AdminRoute>} />
        <Route path="admin/app-users" element={<AdminRoute><AppUsersPage /></AdminRoute>} />
        <Route path="admin/app-users/new" element={<AdminRoute><AppUserDetailPage /></AdminRoute>} />
        <Route path="admin/app-users/:appUserId" element={<AdminRoute><AppUserDetailPage /></AdminRoute>} />
        <Route path="admin/users" element={<AdminRoute><UsersPage /></AdminRoute>} />
        <Route path="admin/users/new" element={<AdminRoute><UserDetailPage /></AdminRoute>} />
        <Route path="admin/users/:userId" element={<AdminRoute><UserDetailPage /></AdminRoute>} />
      </Route>
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
